// @flow
/**
 * Reducer for adding API resource data to the store.
 *
 * Also sets item/collection meta data on success.
 *
 * @todo 18-08-13 ADD TESTS
 */

import {
  fromJS,
  Map,
  Set,
}                            from 'immutable';
import qs                    from 'query-string';
import { handleActions }     from 'redux-actions';
import type {
  FetchRequestAction,
  FetchSuccessAction,
  SaveRequestAction,
  SaveSuccessAction,
}                            from '../actions/apiResourceActions';
import {
  fetchApiResourceFailure,
  fetchApiResourceRequest,
  fetchApiResourceSuccess,
  resetApiResources,
  saveApiResourceFailure,
  saveApiResourceRequest,
  saveApiResourceSuccess,
} from '../actions/apiResourceActions';
import type { ThunkDetails } from '../modules/redux-saga-thunk/redux-saga-thunk.types';
import KEYS                  from './apiDataReducers.keys';
import PATHS                 from './apiDataReducers.paths';
import TYPES                 from './apiDataReducers.types';

// -----------------------------------------------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------------------------------------------


const defaultResourceState = fromJS({
  [KEYS.META]: {
    [KEYS.META_FETCH_STARTED_AT]:   null,
    [KEYS.META_SAVES_IN_PROGRESS]:  {},
    [KEYS.META_FIELDS_BEING_SAVED]: Set(),
    [KEYS.META_UPDATED_AT]:         null,
    [KEYS.META_TYPE]:               null,
    [KEYS.META_ERROR]:              null,
    [KEYS.META_FAILED_ATTEMPTS]:    0,
  },
  [KEYS.DATA]: null,
});


// -----------------------------------------------------------------------------------------------------------------
// Default state helper
// -----------------------------------------------------------------------------------------------------------------

const getResourceIRIStateOrDefault = (state, resourceIRI) => state.get(resourceIRI) || defaultResourceState;

/**
 * Resets the values at each of the `propertyPaths` to the default value
 *
 * @param {Map}     resourceState
 * @param {array[]} propertyPaths
 *
 * @return {Map}
 */
const resetResourceState = (resourceState, ...propertyPaths) => propertyPaths.reduce(
  (state, propertyPath) => state.setIn(propertyPath, defaultResourceState.getIn(propertyPath)),
  resourceState,
);


// -----------------------------------------------------------------------------------------------------------------
// State updaters
// -----------------------------------------------------------------------------------------------------------------

const requestHandler = (state, action: FetchRequestAction<ThunkDetails> | SaveRequestAction<ThunkDetails>) => {

  const {payload: {resourceIRI}} = action;

  let resourceState = resetResourceState(getResourceIRIStateOrDefault(state, resourceIRI), PATHS.META_ERROR);

  switch (action.type) {

    case String(fetchApiResourceRequest):

      return state.set(
        resourceIRI,
        resourceState.setIn(PATHS.META_FETCH_STARTED_AT, (new Date()).toISOString()),
      );

    case String(saveApiResourceRequest):

      const {uuid, dataToSave} = action.payload;

      resourceState = resourceState.mergeIn(
        PATHS.META_SAVES_IN_PROGRESS,
        fromJS({
          [uuid]: {
            [KEYS.META_SAVES_IN_PROGRESS_STARTED_AT]:       (new Date()).toISOString(),
            [KEYS.META_SAVES_IN_PROGRESS_DATA_BEING_SAVED]: dataToSave,
          },
        }),
      );

      return state.set(resourceIRI, resourceState);

    default:

      throw Error(`Unknown action type… ${action.type}`);


  }

};

const successHandler = (state, action: FetchSuccessAction | SaveSuccessAction) => {

  const {payload: {receivedData}, meta: {resourceIRI}} = action;

  const data: Map<string, any> = fromJS(receivedData);

  let resourceState = getResourceIRIStateOrDefault(state, resourceIRI);

  resourceState = resetResourceState(resourceState, PATHS.META_ERROR, PATHS.META_FAILED_ATTEMPTS);

  resourceState = updateInProgressData(resourceState, action);

  if (!data.has('hydra:member')) {

    return state.set(
      data.get('@id'),
      resourceState
        .setIn(PATHS.META_TYPE, TYPES.ITEM)
        .set(KEYS.DATA, data),
    );

  }

  const members = data.get('hydra:member').map((member) => {

    const actionForSingleResource = fetchApiResourceSuccess({
      uuid:         action.meta.uuid,
      resourceIRI:  member.get('@id'),
      receivedData: member,
      ignoreThunk:  true,
    });

    state = successHandler(state, actionForSingleResource);

    return member.get('@id');

  });

  resourceState = resourceState
    .setIn(PATHS.META_TYPE, TYPES.COLLECTION)
    .setIn(PATHS.META_TOTAL_COUNT, data.get('hydra:totalItems'))
    .setIn(
      PATHS.META_PAGE_COUNT,
      // Get the page number from the last page in the collection, or 1 if no last page.
      Number(qs.parseUrl(data.getIn(['hydra:view', 'hydra:last'], '/?page=1')).query['page']),
    )
    .set(KEYS.DATA, members);

  resourceState = updateInProgressData(resourceState, action);

  return state.set(resourceIRI, resourceState);

};

const failureHandler = (state, action) => {

  const {meta: {resourceIRI}, payload: error} = action;

  let resourceState = getResourceIRIStateOrDefault(state, resourceIRI);

  resourceState = resourceState
    .setIn(PATHS.META_ERROR, error)
    .setIn(PATHS.META_FAILED_ATTEMPTS, resourceState.getIn(PATHS.META_FAILED_ATTEMPTS) + 1);

  resourceState = updateInProgressData(resourceState, action);

  return state.set(resourceIRI, resourceState);

};


const updateInProgressData = (resourceState, action) => {

  resourceState = resourceState.setIn(PATHS.META_UPDATED_AT, (new Date()).toISOString());

  switch (action.type) {

    case String(fetchApiResourceSuccess):
    case String(fetchApiResourceFailure):

      return resetResourceState(resourceState, PATHS.META_FETCH_STARTED_AT);


    case String(saveApiResourceSuccess):
    case String(saveApiResourceFailure):

      const uuid = String(action.meta.uuid);

      return resourceState.deleteIn(PATHS.META_SAVES_IN_PROGRESS.concat(uuid));


    default:

      throw Error(`Unknown action type… ${action.type}`);


  }

};


const updateFieldsBeingSavedForHandler = (handler) => (state, action) => {

  const updatedState = handler(state, action);

  let resourceIRI;

  switch (action.type) {

    case String(saveApiResourceRequest):

      resourceIRI = action.payload.resourceIRI;

      break;


    case String(saveApiResourceSuccess):
    case String(saveApiResourceFailure):

      resourceIRI = action.meta.resourceIRI;

      break;


    default:

      throw Error(`Unknown action type… ${action.type}`);

  }

  let resourceState = getResourceIRIStateOrDefault(updatedState, resourceIRI);

  return updatedState.set(
    resourceIRI,
    resourceState.setIn(
      PATHS.META_FIELDS_BEING_SAVED,
      resourceState.getIn(PATHS.META_SAVES_IN_PROGRESS)
        .reduce(
          (fields, saveInProgress) => fields.concat(
            saveInProgress.get(KEYS.META_SAVES_IN_PROGRESS_DATA_BEING_SAVED).keys(),
          ),
          Set(),
        ),
    ),
  );

};


// -----------------------------------------------------------------------------------------------------------------
// Reducers
// -----------------------------------------------------------------------------------------------------------------


/**
 * Handlers for actions
 */
const apiDataReducers = handleActions(
  {

    [String(resetApiResources)]: () => Map(),

    [String(fetchApiResourceRequest)]: requestHandler,

    [String(fetchApiResourceSuccess)]: successHandler,

    [String(fetchApiResourceFailure)]: failureHandler,

    [String(saveApiResourceRequest)]: updateFieldsBeingSavedForHandler(requestHandler),

    [String(saveApiResourceSuccess)]: updateFieldsBeingSavedForHandler(successHandler),

    [String(saveApiResourceFailure)]: updateFieldsBeingSavedForHandler(failureHandler),

  },
  Map(),
);


export default apiDataReducers;


// WEBPACK FOOTER //
// ./src/reducers/apiDataReducers.js